import React, { useEffect, useState, useCallback } from "react";
import {
  Button, Form, Modal, Space, Toast, Tree, TreeSelect, Typography, Spin,
  withField,
} from "@douyinfe/semi-ui";
import { IconPlus, IconRefresh, IconBookOpenStroked } from "@douyinfe/semi-icons";
import { ApiClient, Team } from "../../api/ApiClient";
import ContactsSideSheet from "../../comp/ContactsSideSheet";
import { FormApi } from "@douyinfe/semi-ui/lib/es/form";
import { Value } from "@douyinfe/semi-ui/lib/es/tree";

const { Text, Title } = Typography;
const FormInput = Form.Input;
const FormSelect = Form.Select;
const Option = FormSelect.Option;
const TreeSelectInput = withField(TreeSelect);
const apiClient = ApiClient.getDefault();

interface CrudOption {
  key: string | number;
  label: string;
  value: string | number;
  labelExt?: string;
  children?: CrudOption[];
}

export default function TeamPage() {
  const [teamOpts, setTeamOpts] = useState<any[]>([]);
  const [cityOpts, setCityOpts] = useState<any[]>([]);
  const [updateFormApi, setUpdateFormApi] = useState<any>();
  const [createFormApi, setCreateFormApi] = useState<any>();
  const [team, setTeam] = useState<Team | undefined>();
  const [newTeam, setNewTeam] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState<Value>();
  const [showContacts, setShowContacts] = useState(false);

  const getTeamOpts = async (): Promise<CrudOption[]> => {
    const opts = await apiClient.getTeamAsOpts();
    setTeamOpts(opts);
    return opts;
  };

  const initData = useCallback(async () => {
    setLoading(true);
    try {
      const [opts, cities] = await Promise.all([
        apiClient.getTeamAsOpts(),
        apiClient.getDictAsOpts("city"),
      ]);
      setTeamOpts(opts);
      setCityOpts(cities);
      if (opts.length > 0) {
        handleTreeSelect(opts[0].key as Value, opts);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "团队管理";
    initData();
  }, [initData]);

  const hasChildren = (id: number | undefined): boolean => {
    if (!id) return false;
    const node = teamOpts.find(o => o.value == id);
    return !!(node?.children?.length);
  };

  const handleTreeSelect = async (id: Value | undefined, opts?: CrudOption[]) => {
    if (!id) return;
    setSelectedKey(id);
    try {
      const t = await apiClient.getTeam(parseInt(id as string));
      setTeam(t);
      updateFormApi?.setValues(t);
    } catch {}
  };

  const handleUpdate = async () => {
    await updateFormApi!.validate();
    setUpdating(true);
    try {
      await apiClient.request("/teams", "PUT", null, updateFormApi!.getValues());
      Toast.success("保存成功");
      await getTeamOpts();
      setUpdating(false);
    } catch {
      setUpdating(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "确认删除",
      content: "删除后不可恢复，确定要删除该团队吗？",
      onOk: async () => {
        setUpdating(true);
        try {
          await apiClient.request("/teams/" + updateFormApi!.getValues().id, "DELETE");
          Toast.success("删除成功");
          await initData();
          setUpdating(false);
        } catch {
          setUpdating(false);
        }
      },
    });
  };

  const handleCreate = async () => {
    await createFormApi!.validate();
    setCreating(true);
    try {
      await apiClient.request("/teams", "POST", null, createFormApi!.getValues());
      Toast.success("创建成功");
      setNewTeam(false);
      await initData();
      setCreating(false);
    } catch {
      setCreating(false);
    }
  };

  /** 从 teamOpts 树中查找 labelExt（后端列表接口填充的 userNames） */
  const findLabelExt = (opts: CrudOption[], teamId: number | undefined): string | undefined => {
    if (!teamId) return undefined;
    for (const o of opts) {
      if (o.value == teamId) return o.labelExt;
      if (o.children) {
        const found = findLabelExt(o.children, teamId);
        if (found) return found;
      }
    }
    return undefined;
  };

  const currentUserNames = team ? findLabelExt(teamOpts, team.id) : undefined;

  const parseUserNames = (names: string | undefined): string[] => {
    if (!names) return [];
    return names.split(",").map(n => n.trim()).filter(Boolean);
  };

  /* ==================== 渲染 ==================== */

  return (
    <div style={{ display: "flex", height: "100%", background: "var(--semi-color-bg-1)" }}>
      {/* 左栏：团队树 */}
      <div style={{ width: 600, borderRight: "1px solid var(--semi-color-border)", background: "#fff", display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--semi-color-border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Text strong style={{ fontSize: 15 }}>团队管理</Text>
          <Space>
            <Button size="small" icon={<IconBookOpenStroked />} onClick={() => setShowContacts(true)}>通讯录</Button>
            <Button size="small" icon={<IconPlus />} onClick={() => setNewTeam(true)}>新建</Button>
            <Button size="small" icon={<IconRefresh />} onClick={() => initData()} />
          </Space>
        </div>

        <Spin spinning={loading}>
          <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
            {teamOpts.length > 0 && (
              <Tree
                value={selectedKey}
                expandAll
                onChange={(id) => handleTreeSelect(id)}
                treeData={teamOpts}
                renderLabel={(label, item: any) => {
                  return (
                    <div style={{ display: "flex", width: "100%" }}>
                      <Text ellipsis={{ showTooltip: true }} style={{ width: 100 }}>{label}</Text>
                      <Text disabled ellipsis={{ showTooltip: true }} style={{ marginLeft: 12, flex: 1 }}>{item.labelExt}</Text>
                    </div>
                  );
                }}
              />
            )}
          </div>
        </Spin>
      </div>

      {/* 右栏：详情 + 人员 */}
      <div style={{ flex: 1, overflowY: "auto", padding: 20, minWidth: 0 }}>
        {team ? (
          <>
            {/* 编辑表单 */}
            <div style={{
              background: "#fff", borderRadius: 8, padding: 20,
              border: "1px solid var(--semi-color-border)", marginBottom: 16, maxWidth: 500,
            }}>
              <Text strong style={{ fontSize: 15, display: "block", marginBottom: 16, paddingLeft: 8, borderLeft: "3px solid var(--semi-color-primary)" }}>
                团队信息
              </Text>
              <Form<Team>
                layout="vertical"
                key={team.id}
                initValues={team}
                getFormApi={(api) => { setUpdateFormApi(api); }}
              >
                <Form.Input disabled field="id" label="ID" />
                <Form.Input rules={[{ required: true, message: "请填写名称" }]} field="name" label="名称" />
                <Form.Select rules={[{ required: true, message: "请填写城市" }]} style={{ width: 200 }} field="city" label="城市">
                  {cityOpts.map((c: any) => <Option value={c.value} key={c.value}>{c.label}</Option>)}
                </Form.Select>
                <TreeSelectInput
                  showClear style={{ width: "100%" }} treeData={teamOpts}
                  field="parentId" label="所属团队"
                  filterTreeNode searchPlaceholder="搜索团队"
                />
                <Space style={{ marginTop: 8 }}>
                  <Button loading={updating} theme="solid" onClick={handleUpdate}>保存</Button>
                  {!hasChildren(team.id) && (
                    <Button onClick={handleDelete} type="danger">删除</Button>
                  )}
                </Space>
              </Form>
            </div>

            {/* 人员展示 */}
            <div style={{
              background: "#fff", borderRadius: 8, padding: 20,
              border: "1px solid var(--semi-color-border)", maxWidth: 600,
            }}>
              <Text strong style={{ fontSize: 15, display: "block", marginBottom: 12, paddingLeft: 8, borderLeft: "3px solid var(--semi-color-primary)" }}>
                团队成员
                <Text type="tertiary" size="small" style={{ marginLeft: 8 }}>
                  {parseUserNames(currentUserNames).length} 人
                </Text>
              </Text>

              {(() => {
                const members = parseUserNames(currentUserNames);
                if (members.length === 0) {
                  return <Text type="tertiary" size="small">暂无成员</Text>;
                }
                return (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {members.map((name, i) => (
                      <div key={i} style={{
                        padding: "6px 14px", background: "var(--semi-color-fill-0)",
                        borderRadius: 16, fontSize: 13, display: "inline-flex",
                        alignItems: "center", gap: 6, cursor: "default",
                        border: "1px solid var(--semi-color-border)",
                      }}>
                        <span style={{
                          width: 22, height: 22, borderRadius: "50%",
                          background: "var(--semi-color-primary)", color: "#fff",
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 600, flexShrink: 0,
                        }}>
                          {name.charAt(0)}
                        </span>
                        {name}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </>
        ) : (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <Text type="tertiary">选择左侧团队查看详情</Text>
          </div>
        )}
      </div>

      <ContactsSideSheet visible={showContacts} onClose={() => setShowContacts(false)} />

      {/* 新建弹窗 */}
      <Modal title="新建团队" visible={newTeam} onCancel={() => setNewTeam(false)} onOk={handleCreate} confirmLoading={creating}>
        <Form getFormApi={(api) => setCreateFormApi(api)}>
          <Form.Input rules={[{ required: true, message: "请填写名称" }]} field="name" label="名称" />
          <Form.Select rules={[{ required: true, message: "请填写城市" }]} style={{ width: 200 }} field="city" label="城市">
            {cityOpts.map((c: any) => <Option value={c.value} key={c.value}>{c.label}</Option>)}
          </Form.Select>
          <TreeSelectInput  showClear style={{ width: "100%" }} treeData={teamOpts} field="parentId" label="所属团队"
            filterTreeNode searchPlaceholder="搜索团队" />

        </Form>
      </Modal>
    </div>
  );
}
